// @flow
import { of } from 'rxjs';
import { debounceTime, mergeMap, map, catchError, switchMap, takeUntil } from 'rxjs/operators';
import update from 'immutability-helper';
import { getActiveFlightsApi, getPastFlightsApi, deleteFlightApi, endpoints } from './api';
import { setNewAlert } from '../alert/duck';
import Paginator from '../../common/Paginator';
import { ofType, StateObservable } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Flight } from '../../entities';
import type { RootState } from '../index';

type FlightsAction = {
  +type: string;
  +fetchActiveError?: HTTPError;
  +activeFlights?: Flight[];
  +fetchPastError?: HTTPError;
  +pastFlights?: Flight[];
  +queryFilter?: { [key: string]: string };
  +flightId?: string;
  +deleteActiveError?: HTTPError;
  +resetPagination?: boolean;
};

export type FlightsState = {
  +isFetchingActive?: boolean;
  +activeFlights?: Flight[];
  +fetchActiveError?: HTTPError;

  +isFetchingPast?: boolean;
  +pastFlights?: Flight[];
  +pastFlightsPage?: number;
  +pastFlightsPageCanAsk?: boolean;
  +fetchPastError?: HTTPError;

  +isDeleting?: boolean;
  +successDelete?: boolean;
  +deleteActiveError?: HTTPError;
};

// Actions
const FLIGHTS_RESET_STATE = 'FLIGHTS_RESET_STATE';
const FLIGHTS_FETCH_ACTIVE_REQUEST = 'FLIGHTS_FETCH_ACTIVE_REQUEST';
const FLIGHTS_FETCH_ACTIVE_SUCCESS = 'FLIGHTS_FETCH_ACTIVE_SUCCESS';
const FLIGHTS_FETCH_ACTIVE_ERROR = 'FLIGHTS_FETCH_ACTIVE_ERROR';

const FLIGHTS_FETCH_PAST_REQUEST = 'FLIGHTS_FETCH_PAST_REQUEST';
const FLIGHTS_FETCH_PAST_SUCCESS = 'FLIGHTS_FETCH_PAST_SUCCESS';
const FLIGHTS_FETCH_PAST_ERROR = 'FLIGHTS_FETCH_PAST_ERROR';

const FLIGHTS_DELETE_FLIGHT_REQUEST = 'FLIGHTS_DELETE_FLIGHT_REQUEST';
const FLIGHTS_DELETE_FLIGHT_SUCCESS = 'FLIGHTS_DELETE_FLIGHT_SUCCESS';
const FLIGHTS_DELETE_FLIGHT_ERROR = 'FLIGHTS_DELETE_FLIGHT_ERROR';


// Action Creators
export function fetchActiveFlights(): FlightsAction {
  return {
    type: FLIGHTS_FETCH_ACTIVE_REQUEST,
  };
}

export function fetchActiveFlightsSuccess(activeFlights: Flight[]): FlightsAction {
  return {
    type: FLIGHTS_FETCH_ACTIVE_SUCCESS,
    activeFlights,
  };
}

export function fetchActiveFlightsError(fetchActiveError: HTTPError): FlightsAction {
  return {
    type: FLIGHTS_FETCH_ACTIVE_ERROR,
    fetchActiveError,
  }
}

export function fetchPastFlights(queryFilter?: { [key: string]: string } = {},
                                 resetPagination?: boolean): FlightsAction {
  return {
    type: FLIGHTS_FETCH_PAST_REQUEST,
    queryFilter,
    resetPagination,
  };
}

export function fetchPastFlightsSuccess(pastFlights: Flight[]): FlightsAction {
  return {
    type: FLIGHTS_FETCH_PAST_SUCCESS,
    pastFlights,
  };
}

export function fetchPastFlightsError(fetchPastError: HTTPError): FlightsAction {
  return {
    type: FLIGHTS_FETCH_PAST_ERROR,
    fetchPastError,
  }
}

export function deleteFlight(flightId: string): FlightsAction {
  return {
    type: FLIGHTS_DELETE_FLIGHT_REQUEST,
    flightId,
  }
}

export function deleteFlightSuccess(flightId: string): FlightsAction {
  return {
    type: FLIGHTS_DELETE_FLIGHT_SUCCESS,
    flightId,
  }
}

export function deleteFlightError(error: HTTPError): FlightsAction {
  return {
    type: FLIGHTS_DELETE_FLIGHT_ERROR,
    error,
  }
}

export function resetFlightsState(): FlightsAction {
  return {
    type: FLIGHTS_RESET_STATE,
  };
}

export const actions = {
  FLIGHTS_FETCH_ACTIVE_REQUEST,
  FLIGHTS_FETCH_ACTIVE_SUCCESS,
  FLIGHTS_FETCH_ACTIVE_ERROR,
  FLIGHTS_DELETE_FLIGHT_REQUEST,
  FLIGHTS_DELETE_FLIGHT_SUCCESS,
  FLIGHTS_DELETE_FLIGHT_ERROR,
  FLIGHTS_RESET_STATE,
  fetchActiveFlights,
  fetchActiveFlightsSuccess,
  fetchActiveFlightsError,
  fetchPastFlights,
  fetchPastFlightsSuccess,
  fetchPastFlightsError,
  deleteFlight,
  deleteFlightSuccess,
  deleteFlightError,
  resetFlightsState,
};

export const initialState: FlightsState = {
  pastFlightsPage: 0,
};

const paginator = new Paginator(['pastFlights']);


// Reducer
export default function reducer(state: FlightsState = initialState,
                                action: FlightsAction): FlightsState {
  switch (action.type) {
    case FLIGHTS_FETCH_ACTIVE_REQUEST:
      return {
        isFetchingActive: true,
      };
    case FLIGHTS_FETCH_ACTIVE_SUCCESS:
      return {
        activeFlights: action.activeFlights,
      };
    case FLIGHTS_FETCH_ACTIVE_ERROR:
      return {
        fetchActiveError: action.fetchActiveError,
      };
    case FLIGHTS_FETCH_PAST_REQUEST:
      if (action.resetPagination) {
        paginator.reset('pastFlights');
        return {
          isFetchingPast: true,
          pastFlights: paginator.getItems('pastFlights'),
          pastFlightsPage: paginator.getPage('pastFlights'),
          pastFlightsPageCanAsk: paginator.getCanAsk('pastFlights'),
        };
      }
      return {
        ...state,
        isFetchingPast: true,
      };
    case FLIGHTS_FETCH_PAST_SUCCESS:
      paginator.increment('pastFlights', action.pastFlights, state.pastFlights);
      return {
        pastFlights: paginator.getItems('pastFlights'),
        pastFlightsPage: paginator.getPage('pastFlights'),
        pastFlightsPageCanAsk: paginator.getCanAsk('pastFlights'),
      };
    case FLIGHTS_FETCH_PAST_ERROR:
      return {
        fetchPastError: action.fetchPastError,
        pastFlightsPage: state.pastFlightsPage,
      };
    case FLIGHTS_DELETE_FLIGHT_REQUEST:
      return update(state, {
        $unset: ['deleteActiveError', 'successDelete'],
        isDeleting: { $set: true },
      });
    case FLIGHTS_DELETE_FLIGHT_SUCCESS:
      if (!state.activeFlights) return state;
      // TODO: use and index system
      const index = state.activeFlights.findIndex((flight: Flight, n: number) => flight.id === action.flightId);
      if (index === -1) return state;
      return update(state, {
        $unset: ['isDeleting'],
        successDelete: { $set: true },
        activeFlights: { $splice: [[index, 1]] },
      });
    case FLIGHTS_DELETE_FLIGHT_ERROR:
      return update(state, {
        $unset: ['isDeleting', 'successDelete'],
        deleteActiveError: { $set: action.deleteActiveError }
      });
    case FLIGHTS_RESET_STATE:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchActiveFlightsEpic(action$: Observable<FlightsAction>): Observable<FlightsAction> {
  return action$.pipe(
    ofType(FLIGHTS_FETCH_ACTIVE_REQUEST),
    debounceTime(600),
    mergeMap(() => getActiveFlightsApi({
        limit: 50,
      }).pipe(
      map((response: Flight[]) => fetchActiveFlightsSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchActiveFlightsError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchActiveFlightsError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchActiveFlightsError(e));
      }))
    ));
}

export function fetchPastFlightsEpic(action$: Observable<FlightsAction>,
                                     state$: StateObservable<RootState>): Observable<FlightsAction> {
  return action$.pipe(
    ofType(FLIGHTS_FETCH_PAST_REQUEST),
    debounceTime(600),
    switchMap(({ queryFilter }) => getPastFlightsApi({
        ...queryFilter,
        page: state$.value.flights.pastFlightsPage || paginator.getPage('pastFlights'),
        limit: paginator.getLimit(),
      }).pipe(
      takeUntil(action$.pipe(ofType(FLIGHTS_RESET_STATE))),
      takeUntil(action$.pipe(ofType(FLIGHTS_FETCH_PAST_REQUEST))),
      map((response: Flight[]) => fetchPastFlightsSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchPastFlightsError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchPastFlightsError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchPastFlightsError(e));
      }))
    ));
}

export function deleteActiveFlightEpic(action$: Observable<FlightsAction>): Observable<FlightsAction> {
  return action$.pipe(
    ofType(FLIGHTS_DELETE_FLIGHT_REQUEST),
    mergeMap(({ flightId }) => deleteFlightApi(flightId, {
        route: endpoints.DELETE_FLIGHT.replace(':id', flightId),
      }).pipe(
      map(() => deleteFlightSuccess(flightId)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(deleteFlightError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(deleteFlightError(e),
            setNewAlert('flights', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(deleteFlightError(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/flights/duck.js